import {
	ISagaAction,
	IUserBackdoorLoginPayload,
	IUserLoginPayload,
	IUserRegisterForGamePayload,
	IUserReturnPayload,
	IUserUpdate,
} from "modules/types";
import {call, put} from "typed-redux-saga";
import {Api, ApiError, getLogoutLink, TUserResponse, User} from "modules/utils";
import * as actions from "modules/actions";
import * as Sentry from "@sentry/react";
import {get} from "lodash";
import i18n from "i18next";

export const userBackdoorLoginSaga = function* ({payload}: ISagaAction<IUserBackdoorLoginPayload>) {
	try {
		const response = yield* call(Api.Auth.backdoor, payload);
		ApiError.CHECK(response);

		if (response.result) {
			const {user, session_id} = response.result;
			User.SAVE(user, session_id);
			yield* put(actions.backdoorLoginSuccess(user));
		}
	} catch (e) {
		yield* put(actions.globalError(e as Error));
		yield* put(actions.backdoorLoginFailed(e as Error));
	}
};

export const userLogoutSaga = function* () {
	try {
		const response = yield* call(Api.Auth.logout);
		ApiError.CHECK(response);
		User.CLEAR();
		window.location.href = getLogoutLink();
	} catch (e) {
		yield* put(actions.globalError(e as Error));
		yield* put(actions.userLogoutFailed(e as Error));
	}
};

export const userLoginSaga = function* ({payload}: ISagaAction<IUserLoginPayload>) {
	try {
		const response = yield* call(Api.Auth.login, payload);
		ApiError.CHECK(response);
		if (response.result) {
			const {user, session_id, is_registered} = response.result;

			if (is_registered) {
				User.SAVE(user, session_id);

				yield* put(actions.userLoginSuccess(user));
			} else {
				yield* put(actions.userAskForRegistration(user));
			}
		} else {
			yield* put(actions.userLoginFailed({name: "Error", message: "Login Failed"}));
			Sentry.captureException(response.errors);
		}
	} catch (e) {
		const code = get(e, "code", 0) as number;
		if (code === 515) {
			yield* put(actions.userAskForReturn());
		} else {
			yield* put(actions.userLoginFailed(e as Error));
			yield* put(actions.globalError(e as Error));

			if (code !== 403) {
				// Error from SSO
				Sentry.captureException(e);
			}
		}
	}
};

export const userCreateSaga = function* ({payload}: ISagaAction<FormData>) {
	try {
		const response = yield* call(Api.User.create, payload);

		ApiError.CHECK(response);

		const {user, session_id} = response.result;
		User.SAVE(user, session_id);

		yield* put(actions.userCreateSuccess(user));
	} catch (e) {
		yield* put(actions.userCreateFailed(e as Error));
		yield* put(actions.globalError(e as Error));
	}
};

export const userUpdateSaga = function* (action: ISagaAction<IUserUpdate>) {
	try {
		const response = yield* call(Api.User.update, action.payload);
		ApiError.CHECK(response);

		const {user, session_id} = response.result;
		User.SAVE(user, session_id);

		yield* put(actions.userUpdateSuccess(response.result.user));
	} catch (e) {
		yield* put(actions.userUpdateFailed(e as Error));
	}
};

export const userReturnSaga = function* ({payload}: ISagaAction<IUserReturnPayload>) {
	try {
		const response = yield* call(Api.User.recover, payload);

		ApiError.CHECK(response);

		const {user, session_id} = response.result;
		User.SAVE(user, session_id);

		yield* put(actions.userReturnSuccess(user));
		yield* put(actions.fetchMyTeam(undefined));
	} catch (e) {
		yield* put(actions.userCreateFailed(e as Error));
		yield* put(actions.globalError(e as Error));
	}
};

export const userRegisterForGameSaga = function* ({payload}: ISagaAction<IUserReturnPayload>) {
	try {
		const params: IUserRegisterForGamePayload = {
			...payload,
			terms: 1,
		};
		const response: TUserResponse = yield* call(Api.User.register_for_game, params);

		ApiError.CHECK(response);
		if (response.result) {
			const {user, session_id} = response.result;
			User.SAVE(user, session_id);

			yield* put(actions.userRegisterForGameSuccess(user));
		}
	} catch (e) {
		yield* put(actions.userCreateFailed(e as Error));
		yield* put(actions.globalError(e as Error));
	}
};

export const userFetchSaga = function* () {
	try {
		const response = yield* call(Api.User.show_my);
		ApiError.CHECK(response);

		if (response.result) {
			const {user, session_id, is_registered} = response.result;
			if (is_registered) {
				User.SAVE(user, session_id);
				yield* put(actions.userFetchSuccess(response.result.user));
				void i18n.changeLanguage(response.result.user.lang);
			} else {
				yield* put(actions.userAskForRegistration(user));
			}
		}
	} catch (e) {
		const code = get(e, "code", 0) as number;
		if (code === 515) {
			yield* put(actions.userAskForReturn());
		} else {
			yield* put(actions.userFetchFailed(e as Error));
		}
	}
};
